/**
 * useGenerateActivityFlow — « Générer une activité », le `+` de la liste d'activités, côté parent.
 *
 * Le legacy est `tutor/students/GenerateActivityModal.js` (3 507 l.), une modale de tuteur montée
 * telle quelle dans l'espace parent. Les appels sont les siens, dans son ordre :
 *  - catalogue des matières de la classe : `getSchoolSubjects` (`GET /api/school-subjects?class=`),
 *    appelé en direct comme le legacy, avec repli silencieux sur le catalogue global ;
 *  - chapitres : `getChaptersAsTutor` (`page_size: 100`) ; compétences : `getChapterSkillParts` ;
 *  - lecture d'un fichier : `analyzeMaterial` derrière le téléversement vapor ;
 *  - repli inter-classes : `searchCurriculum` ;
 *  - génération : les trois `generateMaterialsSkillsPart{1,2,3}` d'une même fournée ;
 *  - activités similaires : les propositions « révision » ou les trois variantes d'entraînement ;
 *  - création : `createActivityAsTutor` — en mode parent l'identifiant de l'URL est celui de
 *    l'enfant lui-même, que la règle `manageActivitiesForStudent` autorise (`UsersPolicy:80-93`).
 *
 * Le réducteur ci-dessous est pur et exporté : chaque transition se joue dans un test, sans écran.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import * as DocumentPicker from "expo-document-picker"

import { useRequestsBatch } from "../../../../common/hooks/useRequestsBatch"
import { studentClasses } from "../../../../data/constants/formConstants"
import { apiIdle, apiRequest } from "../../../../data/redux/actions/api"
import { activitiesService } from "../../../../data/services/activitiesService"
import { chaptersService } from "../../../../data/services/chaptersService"
import { materialsService } from "../../../../data/services/materialsService"
import { schoolSubjectsService } from "../../../../data/services/schoolSubjectsService"
import { type GenerateActivityFlowProps } from "../../../../design-system/templates/GenerateActivityFlow"
import {
    ACTIVITY_DURATION_OPTIONS,
    ACTIVITY_LEVEL_OPTIONS,
    analysisSuggestion,
    createFromFileBody,
    createResultBody,
    crossClassMatch,
    DEFAULT_SCHOOL_SUBJECT_ID,
    defaultMaterialDuration,
    findGenerationSubject,
    generateMaterialsBody,
    generationScopeLabel,
    lastFinishedActivitySubjectId,
    missingGenerationField,
    newBatchId,
    similarExercisesBody,
    toUIActivityClassOptions,
    toUIActivitySkillParts,
    toUIGeneratedActivity,
    type ApiAnalyzedMaterial,
    type ApiCurriculumSearchRow,
    type ApiGeneratedMaterial,
    type ApiSkillPart,
    type CrossClassMatch,
} from "../../adapters/parent/Activities/activityGenerationAdapter"
import {
    toUISectionedChapters,
    type ApiCatalogChapter,
} from "../../adapters/parent/SchoolAssessments/sectionedChapterAdapter"
import { toUISubject, type ApiSchoolSubject } from "../../adapters/parent/shared/schoolSubjectAdapter"
import {
    type UIActivityAnalysis,
    type UIActivityFile,
    type UIActivityGenerationDraft,
    type UIActivityGenerationStep,
    type UIGeneratedActivity,
} from "../types"
import { apiErrorMessage } from "./apiErrorMessage"
import { useGesture } from "./gestureTracking"
import { useApiMutation } from "./useApiMutation"
import { selectAccount, selectDisplayedStudent } from "../../view-models/shared/displayedStudent"

/** Le legacy nomme sa fournée de téléversement ainsi — gardé tel quel. */
const UPLOAD_FORM_ID = "IMAGE_MATERIAL_UPLOAD"
/** Les seuls types que la zone de dépôt accepte (`ImageUploadZone.ACCEPTED_TYPES_MOBILE`). */
const ACCEPTED_FILE_TYPES = ["image/*", "application/pdf"]

const CREATE_FAILED = "Impossible de créer cette activité. Réessayez dans un instant."

/** Le brouillon vide : les valeurs par défaut du formulaire legacy, avant tout cadrage. */
export const emptyGenerationDraft: UIActivityGenerationDraft = {
    className: null,
    subjectId: null,
    chapterId: null,
    skillPartPositions: [],
    level: "2",
    duration: "5",
    instructions: "",
    personalizeWithInterests: false,
}

export type GenerateActivityFlowState = {
    visible: boolean
    step: UIActivityGenerationStep
    draft: UIActivityGenerationDraft
    file: UIActivityFile | null
    /** Un envoi est en cours — c'est le bouton de l'étape qui tourne. */
    generating: boolean
    /** L'activité a été créée depuis le fichier : le bouton le dit et ne repart pas. */
    created: boolean
    error: string | null
}

export const initialGenerateActivityFlowState: GenerateActivityFlowState = {
    visible: false,
    step: "form",
    draft: emptyGenerationDraft,
    file: null,
    generating: false,
    created: false,
    error: null,
}

export type GenerateActivityFlowAction =
    | { type: "OPEN"; draft: UIActivityGenerationDraft }
    | { type: "SET_DRAFT"; draft: UIActivityGenerationDraft }
    | { type: "PICK_FILE"; file: UIActivityFile }
    | { type: "REMOVE_FILE" }
    | { type: "OPEN_SIMILAR" }
    | { type: "SHOW_RESULTS" }
    | { type: "SUBMIT" }
    | { type: "CREATED" }
    | { type: "FAILED"; message: string }
    | { type: "BACK" }
    | { type: "CLOSE" }

export function generateActivityFlowReducer(
    state: GenerateActivityFlowState,
    action: GenerateActivityFlowAction
): GenerateActivityFlowState {
    switch (action.type) {
        case "OPEN":
            return { ...initialGenerateActivityFlowState, visible: true, draft: action.draft }

        case "SET_DRAFT":
            return { ...state, draft: action.draft, error: null }

        // Le fichier lu répond déjà au type et aux instructions : l'étape qui suit ne demande
        // plus que le chapitre et la durée.
        case "PICK_FILE":
            return { ...state, step: "file", file: action.file, created: false, error: null }

        case "REMOVE_FILE":
            return { ...state, step: "form", file: null, created: false, error: null }

        case "OPEN_SIMILAR":
            return state.file == null ? state : { ...state, step: "similar", error: null }

        case "SHOW_RESULTS":
            return { ...state, step: "results", generating: false, error: null }

        case "SUBMIT":
            return { ...state, generating: true, error: null }

        case "CREATED":
            return { ...state, generating: false, created: true, error: null }

        case "FAILED":
            return { ...state, generating: false, error: action.message }

        case "BACK":
            if (state.step === "results") return { ...state, step: state.file != null ? "file" : "form", error: null }
            if (state.step === "similar") return { ...state, step: "file", error: null }
            if (state.step === "file") return { ...state, step: "form", file: null, created: false, error: null }
            return state

        case "CLOSE":
            return initialGenerateActivityFlowState

        default:
            return state
    }
}

/**
 * Les trois cartes d'une fournée, et le service qui remplit chacune. Le legacy a une fonction
 * de service par emplacement — elles existent pour que la tranche Redux sache à quelle carte
 * répondre, et c'est le même rôle ici.
 */
type SlotKey =
    "skills1" | "skills2" | "skills3" | "conceptReview" | "methodReview" | "same" | "sameDiversified" | "otherConcepts"

type ApiSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    data?: unknown
    all_data?: unknown
    urlParams?: { school_subject_id?: string | number; class?: string }
}

/** Le cadrage dont on attend la liste des chapitres. */
type ChaptersScope = { class: string; subject: string }

/** La réponse est-elle celle du cadrage demandé, et pas celle d'une lecture d'avant ? */
function answersScope(asked: ChaptersScope, answered: ApiSlice["urlParams"]): boolean {
    return answered?.class === asked.class && String(answered?.school_subject_id) === asked.subject
}

type SettleSuccess = (slot: SlotKey, material: ApiGeneratedMaterial) => void
type SettleError = (slot: SlotKey) => void

/**
 * Une carte, une tranche : le service dit à quelle carte la réponse appartient, et `latestOnly` que
 * seule la réponse du dernier lancement la remplit. Sans lui, une génération relancée avant sa réponse
 * (un autre élève, « Autres énoncés ») recevait l'énoncé du premier lancement, et celui du second était perdu.
 */
function useMaterialSlot(
    service: { name: string },
    slot: SlotKey,
    onSuccess: SettleSuccess,
    onError: SettleError
): (routeParams: Record<string, unknown>, body: Record<string, unknown>) => void {
    const { start } = useApiMutation({
        service,
        latestOnly: true,
        onSuccess: (data) => {
            const payload = data as { data?: ApiGeneratedMaterial } | ApiGeneratedMaterial | undefined
            const material = ((payload as { data?: ApiGeneratedMaterial })?.data ?? payload) as ApiGeneratedMaterial
            onSuccess(slot, material ?? {})
        },
        onError: () => onError(slot),
    })
    return start
}

export type UseGenerateActivityFlowArgs = {
    /** L'enfant — son identifiant part dans l'URL de création. */
    studentId?: string | number | null
    /** Rejoué après une création : la liste doit montrer la nouvelle activité. */
    onCreated?: () => void
    /**
     * Ce qui suit est propre au tuteur (le live) ; absent, le parent et l'élève gardent leur geste à
     * l'identique. Les élèves d'une activité commune (`GenerateActivityModal.forStudents`) : dès qu'il y en
     * a un, la création part par `activityTodoForStudents` avec `student_ids` et `live_session_id`, même
     * pour un seul inscrit (`isActivityForStudents`) ; `studentId` est l'élève représentatif des lectures
     * (`resolvedStudent`, le premier).
     */
    targetStudentIds?: (string | number)[]
    /** La séance du live (`live_session_id` d'une activité commune, `props.isLiveMode`). */
    liveSessionId?: string | number | null
    /** Le quiz de prérequis offert sur chaque carte (la salle `prerequisite-quiz`). Défaut : non. */
    quizAvailable?: boolean
    /**
     * La réponse d'une création, telle que le legacy la lit pour prévenir l'élève en live (`newTodo`) :
     * l'activité (`data`) pour un élève, `{ new_activities }` pour une activité commune.
     */
    onCreatedActivity?: (response: unknown) => void
    /**
     * Une création refusée (réseau, 422) : l'écran la dit, avec la raison du serveur s'il en donne une (`error`, l'enveloppe
     * de l'api). L'élève et le parent lisent `error` dans l'étape.
     */
    onCreateFailed?: (error: unknown) => void
    /**
     * Un fichier choisi au sélecteur passe d'abord ici : rendre `true` le garde (le tuteur recadre une
     * photo au web, puis la joint par `attachFile`). Absent, ou `false` : il est joint aussitôt, comme
     * pour l'élève et le parent.
     */
    holdFile?: (file: UIActivityFile) => boolean
}

export type UseGenerateActivityFlowResult = {
    /** Ouvre le geste sur son formulaire, cadré sur la classe et la dernière matière de l'enfant. */
    open: () => void
    /**
     * Joint un fichier venu d'ailleurs que le sélecteur (un collage, un dépôt, une photo du champion) :
     * la même étape que `onPickFile`, sans le sélecteur.
     */
    attachFile: (file: UIActivityFile) => void
    /**
     * Part d'une activité existante pour en générer de semblables (« Générer à partir de l'exo ») : son
     * support tient lieu de fichier lu (`materialFromImage` du legacy), et `onGenerateSimilar` le reprend.
     */
    seedMaterial: (material: { id: string | number; title?: string | null }) => void
    /** Retire le support posé par `seedMaterial` : la demande suivante n'est plus une similaire. */
    clearMaterial: () => void
    /**
     * Une création attend-elle sa réponse, de ces cartes ou d'un lot d'avant (`open`, « Autres énoncés » vident
     * les cartes, pas la création en vol) ? Une seconde ne part pas : leurs réponses se croiseraient.
     */
    isCreating: () => boolean
    /** Les props de `<GenerateActivityFlow>`, prêtes à être étalées. */
    generate: GenerateActivityFlowProps
}

/**
 * La création part-elle en activité commune ? La condition de `GenerateActivityModal.createActivity`
 * (`forStudents && selectedStudentIds.length > 0`) : `forStudents` y est le geste « Activité commune »
 * (`LiveModeFooter`, `StudentsList`), pas un nombre d'élèves. Le geste porte ici ses élèves
 * (`targetStudentIds`) : un seul inscrit (une séance individuelle, un groupe réduit à un élève) part par
 * le même `activityTodoForStudents`, avec son `live_session_id`, sinon l'activité perd sa salle.
 */
export function isActivityForStudents(targetStudentIds: readonly (string | number)[] | null | undefined): boolean {
    return targetStudentIds != null && targetStudentIds.length > 0
}

/**
 * L'élève tel que le porte sa séance : en live, le tuteur n'a pas forcément chargé « Mes élèves » (la liste
 * du compte est vide après une connexion), et l'ancienne modale lisait déjà la classe sur la séance.
 */
function liveRosterStudent(state: unknown, liveSessionId: unknown, studentId: unknown) {
    if (liveSessionId == null || studentId == null) return undefined
    const account = selectAccount(state as Parameters<typeof selectAccount>[0]) as {
        live_sessions?: { id?: unknown; users?: { id?: unknown }[] | null }[] | null
    } | null
    return account?.live_sessions
        ?.find((session) => String(session?.id) === String(liveSessionId))
        ?.users?.find((user) => String(user?.id) === String(studentId)) as ReturnType<typeof selectDisplayedStudent>
}

/**
 * La classe d'une activité commune : celle que partagent tous les élèves visés, lue comme celle d'un seul
 * (« Mes élèves », sinon la séance) ; aucune dès qu'un élève n'a pas de classe connue ou qu'ils diffèrent.
 * `GenerateActivityModal` (`forStudents`, `student={null}`) ouvrait sur une classe vide et faisait choisir
 * dans son en-tête « Classe · Matière » : le sélecteur reste le recours, la classe commune évite le geste
 * quand elle ne fait pas de doute.
 */
export function commonTargetClass(
    state: unknown,
    liveSessionId: unknown,
    targetStudentIds: readonly (string | number)[]
): string | null {
    let common: string | null = null
    for (const id of targetStudentIds) {
        const target = (selectDisplayedStudent(state, id) ?? liveRosterStudent(state, liveSessionId, id)) as {
            profile?: { class?: string | null } | null
        } | null
        const className = target?.profile?.class ?? null
        if (className == null || (common != null && common !== className)) return null
        common = className
    }
    return common
}

export function useGenerateActivityFlow(args: UseGenerateActivityFlowArgs = {}): UseGenerateActivityFlowResult {
    const {
        studentId,
        onCreated,
        targetStudentIds,
        liveSessionId,
        quizAvailable = false,
        onCreatedActivity,
        onCreateFailed,
        holdFile,
    } = args
    const holdFileRef = useRef(holdFile)
    holdFileRef.current = holdFile
    const forStudents = isActivityForStudents(targetStudentIds)
    const forStudentsRef = useRef(forStudents)
    forStudentsRef.current = forStudents
    // Le quiz de prérequis demandé carte par carte (`pendingQuizMaterials` du legacy) ; jamais sans la salle.
    // Coché par défaut (weekly du 03/11, W17) : une carte n'est sans quiz que si le masteur l'a décoché.
    const [quizByCard, setQuizByCard] = useState<Record<string, boolean>>({})
    const dispatch = useDispatch()

    const [state, dispatchUi] = useReducer(generateActivityFlowReducer, initialGenerateActivityFlowState)
    // Le brouillon au dernier rendu : les effets et les envois le lisent sans se redéclarer à
    // chaque frappe.
    const draftRef = useRef(state.draft)
    draftRef.current = state.draft
    const [scopedSubjects, setScopedSubjects] = useState<ApiSchoolSubject[] | null>(null)
    const [chapters, setChapters] = useState<ApiCatalogChapter[]>([])
    // La liste n'a pas pu se charger : un catalogue vide n'en est pas un, le chapitre reste exigé et
    // le champ propose de réessayer. Le compteur relance la même lecture.
    const [chaptersFailed, setChaptersFailed] = useState(false)
    const [chaptersAttempt, setChaptersAttempt] = useState(0)
    const [analysisStatus, setAnalysisStatus] = useState<UIActivityAnalysis["status"]>("idle")
    const [analyzed, setAnalyzed] = useState<ApiAnalyzedMaterial | null>(null)
    const [selectedBySam, setSelectedBySam] = useState(false)
    const [crossClass, setCrossClass] = useState<UIActivityAnalysis["crossClass"] | null>(null)
    const [crossClassLoading, setCrossClassLoading] = useState(false)
    const [crossClassDismissed, setCrossClassDismissed] = useState(false)
    const [crossClassPick, setCrossClassPick] = useState<CrossClassMatch | null>(null)
    const [results, setResults] = useState<UIGeneratedActivity[]>([])

    const student = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => selectDisplayedStudent(s, studentId) ?? liveRosterStudent(s, liveSessionId, studentId)
    )
    // Une activité commune n'a pas d'élève (`student={null}` dans le legacy) : ni centres d'intérêt, ni
    // matière d'une dernière activité ; l'historique anti-répétition et les chapitres lisent tous les élèves.
    const targetKey = forStudents ? (targetStudentIds ?? []).join(",") : null
    const targetClass = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => (forStudents ? commonTargetClass(s, liveSessionId, targetStudentIds ?? []) : null)
    )
    const studentInterests: string | null = forStudents ? null : (student?.profile?.interests ?? null)
    // `historyStudentIds` du legacy : en activité commune, l'union des historiques des élèves visés.
    const historyIds = useMemo(
        () => (targetKey != null ? { student_ids: targetStudentIds } : null),
        [targetKey, targetStudentIds]
    )
    const globalSubjects = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => s?.data?.staticData?.school_subjects as ApiSchoolSubject[] | undefined
    )
    const skillPartsByChapterId = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => s?.data?.chapters?.skillPartsByChapterId as Record<string, ApiSkillPart[]> | undefined
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const chaptersReq = useSelector((s: any) => s?.api?.getChaptersAsTutor as ApiSlice | undefined)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const skillPartsReq = useSelector((s: any) => s?.api?.getChapterSkillParts as ApiSlice | undefined)

    const subjects = useMemo(() => scopedSubjects ?? globalSubjects ?? [], [scopedSubjects, globalSubjects])
    const { className, subjectId, chapterId } = state.draft

    // ── Catalogue des matières de la classe. Appel direct, comme le legacy : la réponse n'a
    // qu'un lecteur et n'a rien à faire dans une tranche partagée. Échec ⇒ catalogue global.
    useEffect(() => {
        if (!state.visible || className == null) {
            setScopedSubjects(null)
            return
        }
        let cancelled = false
        setScopedSubjects(null)
        schoolSubjectsService
            .getSchoolSubjects({}, {}, { class: className })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((response: any) => {
                if (cancelled) return
                const data = response?.data?.data ?? response?.data
                setScopedSubjects(Array.isArray(data) ? data : null)
            })
            .catch(() => {
                if (!cancelled) setScopedSubjects(null)
            })
        return () => {
            cancelled = true
        }
    }, [state.visible, className])

    // La puce de matière se compare en `===` : l'identifiant du brouillon doit être celui que
    // porte le catalogue, pas sa forme texte. Le legacy comparait en `toString()` de part et
    // d'autre, il n'avait pas ce souci ; le brouillon est semé avant que le catalogue arrive.
    useEffect(() => {
        if (!state.visible || subjectId == null) return
        const match = findGenerationSubject(subjects, subjectId)
        if (match?.id == null || match.id === subjectId) return
        dispatchUi({ type: "SET_DRAFT", draft: { ...draftRef.current, subjectId: match.id } })
    }, [state.visible, subjects, subjectId])

    // ── Chapitres de la classe et de la matière. `page_size: 100` est le plafond du backend, et
    // le legacy le pose parce que 20 cachait des chapitres en 1ère / Français.
    // Le cadrage demandé, pour reconnaître SA réponse : changer de classe puis de matière lance
    // deux appels, et le premier ne doit pas remplir la liste du second.
    const awaitingChaptersRef = useRef<ChaptersScope | null>(null)
    useEffect(() => {
        setChaptersFailed(false)
        if (!state.visible || className == null || subjectId == null) {
            setChapters([])
            return
        }
        awaitingChaptersRef.current = { class: className, subject: String(subjectId) }
        // En activité commune, le legacy lit les chapitres sans élève dans l'adresse (`{ id: student?.id }`,
        // `student` nul : `/students/chapters`) et passe les élèves en `student_ids` joints par des virgules.
        dispatch(
            apiRequest(
                chaptersService.getChaptersAsTutor,
                { id: targetKey != null ? undefined : studentId },
                {},
                {
                    school_subject_id: subjectId,
                    class: className,
                    page_size: 100,
                    ...(targetKey != null && { student_ids: targetKey }),
                }
            ) as never
        )
    }, [dispatch, state.visible, className, subjectId, studentId, chaptersAttempt, targetKey])

    useEffect(() => {
        const asked = awaitingChaptersRef.current
        if (asked == null || !chaptersReq?.success || !answersScope(asked, chaptersReq.urlParams)) return
        awaitingChaptersRef.current = null
        const payload = chaptersReq.data as { chapters?: ApiCatalogChapter[] } | undefined
        setChapters(payload?.chapters ?? [])
        dispatch(apiIdle(chaptersService.getChaptersAsTutor) as never)
    }, [dispatch, chaptersReq?.success, chaptersReq?.data, chaptersReq?.urlParams])

    useEffect(() => {
        const asked = awaitingChaptersRef.current
        // L'échec d'une lecture d'avant (une autre classe, une autre matière) n'est pas celui de la
        // liste attendue : le prendre dirait « pas pu se charger » d'une liste qui arrive.
        if (asked == null || !chaptersReq?.error || !answersScope(asked, chaptersReq.urlParams)) return
        awaitingChaptersRef.current = null
        setChapters([])
        setChaptersFailed(true)
        dispatch(apiIdle(chaptersService.getChaptersAsTutor) as never)
    }, [dispatch, chaptersReq?.error, chaptersReq?.urlParams])

    // ── Compétences du chapitre. La réponse est mise en cache par Redux ; une liste vide est
    // traitée comme une absence, parce qu'elle l'est tant que le curriculum ne les a pas posées.
    useEffect(() => {
        if (!state.visible || chapterId == null) return
        const cached = skillPartsByChapterId?.[String(chapterId)]
        if (cached && cached.length > 0) return
        dispatch(apiRequest(chaptersService.getChapterSkillParts, { chapterId }) as never)
        // Le cache est la seule lecture : le relire à chaque frappe relancerait l'appel.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, state.visible, chapterId])

    // ── Lecture d'un fichier par le champion : téléversement vapor puis `POST /api/v2/materials/analyze`.
    // Une pièce de la conversation est déjà sur le serveur : la lecture part avec son `message_id`, sans
    // téléversement (`hasMessageId` du legacy).
    const fileMessageId = state.file?.messageId ?? null
    const filesToUpload = useMemo(() => {
        if (state.file == null || state.file.messageId != null) return []
        return [
            {
                uri: state.file.uri,
                name: state.file.name ?? "document",
                type: state.file.mimeType ?? "image/jpeg",
            },
        ]
    }, [state.file])

    // La lecture en vol ne vaut plus : son fichier a été retiré (`onRemoveFile`). Une lecture remplacée par celle d'un
    // autre fichier ne répond jamais : `useRequestsBatch` ne tient qu'un envoi et n'accepte que la réponse du sien.
    const discardUploadRef = useRef(false)
    const [, , startUpload] = useRequestsBatch(
        [],
        filesToUpload,
        UPLOAD_FORM_ID,
        {
            service: materialsService.analyzeMaterial,
            params: {},
            data: {
                student_id: studentId,
                student_level: className ?? student?.profile?.class ?? "3ème",
                school_subject_id: subjectId != null ? Number(subjectId) : null,
                ...(fileMessageId != null && { message_id: fileMessageId }),
            },
            tmpFilesKey: "image_files",
            tmpFilesFormatter: (tmpKey: string) => tmpKey,
            additionalData: [],
            onSuccessBeforeNext: (responseData: unknown) => {
                // Le fichier a été retiré pendant la lecture (une « similaire » a pris sa place, live du 04/11) : sa
                // lecture n'écrase pas le support d'à présent.
                if (discardUploadRef.current) return false
                const payload = responseData as { data?: { data?: ApiAnalyzedMaterial } | ApiAnalyzedMaterial }
                const material = (payload?.data as { data?: ApiAnalyzedMaterial })?.data ?? payload?.data ?? payload
                setAnalyzed(material as ApiAnalyzedMaterial)
                setAnalysisStatus("done")
                return false
            },
            // Jamais `showErrorDialog` : il rend un `DialogM` legacy, la chrome que les ports
            // refusent. L'échec passe en encart d'analyse, dans l'étape.
            onError: () => {
                if (discardUploadRef.current) return
                setAnalyzed(null)
                setAnalysisStatus("error")
            },
        },
        []
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any as [number, boolean, () => void]

    const startedUploadRef = useRef<string | null>(null)
    useEffect(() => {
        const uri = state.file?.uri
        if (uri == null || className == null || subjectId == null) return
        if (startedUploadRef.current === uri) return
        startedUploadRef.current = uri
        discardUploadRef.current = false
        dispatch(apiIdle(materialsService.analyzeMaterial) as never)
        setAnalyzed(null)
        setSelectedBySam(false)
        setCrossClass(null)
        setCrossClassDismissed(false)
        setCrossClassLoading(false)
        setAnalysisStatus("analyzing")
        startUpload()
    }, [dispatch, state.file?.uri, className, subjectId, startUpload])

    // Le chapitre lu avec une confiance haute est posé d'office, une seule fois par analyse :
    // sans cette garde, effacer la puce la ferait revenir au rendu suivant.
    const appliedAnalysisRef = useRef<string | number | null>(null)

    useEffect(() => {
        if (analysisStatus !== "done" || analyzed == null) return
        const suggestion = analysisSuggestion(analyzed)
        if (suggestion.confidence !== "high" || suggestion.chapterId == null) return
        const key = analyzed.id ?? suggestion.title
        if (key == null || appliedAnalysisRef.current === key) return
        appliedAnalysisRef.current = key
        if (draftRef.current.chapterId != null) return
        setSelectedBySam(true)
        dispatchUi({
            type: "SET_DRAFT",
            draft: {
                ...draftRef.current,
                chapterId: suggestion.chapterId,
                level: suggestion.level ?? draftRef.current.level,
            },
        })
    }, [analysisStatus, analyzed])

    // Confiance haute mais aucun chapitre dans la classe : le contenu appartient peut-être à une
    // autre classe. Même repli que le legacy, sur le titre lu.
    useEffect(() => {
        if (analysisStatus !== "done" || analyzed == null) return
        const suggestion = analysisSuggestion(analyzed)
        if (suggestion.confidence !== "high" || suggestion.chapterId != null) return
        if (!suggestion.title?.trim() || subjectId == null) return
        if (crossClassDismissed || chapterId != null) return

        let cancelled = false
        setCrossClassLoading(true)
        chaptersService
            .searchCurriculum({}, {}, { q: suggestion.title, school_subject_id: Number(subjectId), limit: 5 })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((response: any) => {
                if (cancelled) return
                const rows = (response?.data?.data?.results ?? []) as ApiCurriculumSearchRow[]
                const match = crossClassMatch(rows, className)
                setCrossClassPick(match)
                setCrossClass(
                    match != null
                        ? {
                              className: match.className,
                              subjectName: match.subjectName,
                              chapterName: match.chapterName,
                          }
                        : null
                )
            })
            .catch(() => {
                if (!cancelled) setCrossClass(null)
            })
            .finally(() => {
                if (!cancelled) setCrossClassLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [analysisStatus, analyzed, subjectId, className, chapterId, crossClassDismissed])

    // ── Les huit emplacements de carte. Chaque service répond à une carte, et c'est ce que le
    // legacy encode déjà en ayant une fonction de service par variation.
    const slotCardRef = useRef<Partial<Record<SlotKey, number>>>({})
    /** Le support servi par chaque carte : « Créer » a besoin de son identifiant, qu'on n'affiche pas. */
    const materialByCardRef = useRef<Record<string, ApiGeneratedMaterial>>({})

    const settleSuccess = useCallback<SettleSuccess>(
        (slot, material) => {
            const cardId = slotCardRef.current[slot]
            if (cardId == null) return
            materialByCardRef.current[String(cardId)] = material
            setResults((previous) =>
                previous.map((card) =>
                    card.id === cardId
                        ? {
                              ...card,
                              ...toUIGeneratedActivity(material, {
                                  fallbackSubject: findGenerationSubject(subjects, draftRef.current.subjectId),
                              }),
                              // Le libellé de variation est posé au lancement ; la réponse le rend
                              // nul et l'écraserait — même garde que le legacy.
                              variationLabel: card.variationLabel,
                              id: card.id,
                              status: "ready" as const,
                          }
                        : card
                )
            )
        },
        [subjects]
    )

    const settleError = useCallback<SettleError>((slot) => {
        const cardId = slotCardRef.current[slot]
        if (cardId == null) return
        setResults((previous) =>
            previous.map((card) => (card.id === cardId ? { ...card, status: "error" as const } : card))
        )
    }, [])

    const startSkills1 = useMaterialSlot(
        activitiesService.generateMaterialsSkillsPart1,
        "skills1",
        settleSuccess,
        settleError
    )
    const startSkills2 = useMaterialSlot(
        activitiesService.generateMaterialsSkillsPart2,
        "skills2",
        settleSuccess,
        settleError
    )
    const startSkills3 = useMaterialSlot(
        activitiesService.generateMaterialsSkillsPart3,
        "skills3",
        settleSuccess,
        settleError
    )
    const startConceptReview = useMaterialSlot(
        activitiesService.generatePropositionConceptReview,
        "conceptReview",
        settleSuccess,
        settleError
    )
    const startMethodReview = useMaterialSlot(
        activitiesService.generatePropositionMethodReview,
        "methodReview",
        settleSuccess,
        settleError
    )
    const startSame = useMaterialSlot(
        activitiesService.generateSimilarExercisesSameDifficulty,
        "same",
        settleSuccess,
        settleError
    )
    const startSameDiversified = useMaterialSlot(
        activitiesService.generateSimilarExercisesSameDifficultyDiversified,
        "sameDiversified",
        settleSuccess,
        settleError
    )
    const startOtherConcepts = useMaterialSlot(
        activitiesService.generateSimilarExercisesOtherConcepts,
        "otherConcepts",
        settleSuccess,
        settleError
    )

    // ── Création : la même tranche sert la carte d'un résultat et le fichier lu, comme dans le
    // legacy. Un ref dit laquelle des deux attend sa réponse — une seule à la fois : une deuxième
    // création partie avant la réponse de la première écrasait son attente, et la réponse de la
    // première marquait la seconde. Les cartes se numérotent 1 à 3 à chaque fournée : la carte
    // attendue est celle de sa fournée (`lot`), pas celle de même numéro d'une fournée relancée depuis.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const createReq = useSelector((s: any) => s?.api?.createActivityAsTutor as ApiSlice | undefined)
    const awaitingCreateRef = useRef<
        { kind: "file" } | { kind: "result"; cardId: string | number; lot: number } | null
    >(null)
    const handledCreateErrorRef = useRef(false)
    const lotRef = useRef(0)
    const onCreateFailedRef = useRef(onCreateFailed)
    onCreateFailedRef.current = onCreateFailed
    const gesture = useGesture("activity.create")
    /** La carte attendue, si sa fournée est encore celle affichée. */
    const markCard = useCallback((pending: { cardId: string | number; lot: number }, creation: "created" | "idle") => {
        if (pending.lot !== lotRef.current) return
        setResults((previous) => previous.map((card) => (card.id === pending.cardId ? { ...card, creation } : card)))
    }, [])

    useEffect(() => {
        if (awaitingCreateRef.current == null || !createReq?.success) return
        const pending = awaitingCreateRef.current
        awaitingCreateRef.current = null
        if (pending.kind === "file") dispatchUi({ type: "CREATED" })
        else markCard(pending, "created")
        gesture.succeeded()
        onCreated?.()
        onCreatedActivity?.(createReq.data ?? createReq.all_data)
        dispatch(apiIdle(activitiesService.createActivityAsTutor) as never)
    }, [
        dispatch,
        onCreated,
        onCreatedActivity,
        createReq?.success,
        createReq?.data,
        createReq?.all_data,
        gesture,
        markCard,
    ])

    // La même attente, pour une activité commune (`activityTodoForStudents`) : sa réponse porte
    // `new_activities`, une par élève (`data || all_data`, comme le legacy).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const forStudentsReq = useSelector((s: any) => s?.api?.activityTodoForStudents as ApiSlice | undefined)
    useEffect(() => {
        if (awaitingCreateRef.current == null || !forStudentsReq?.success) return
        const pending = awaitingCreateRef.current
        awaitingCreateRef.current = null
        if (pending.kind === "file") dispatchUi({ type: "CREATED" })
        else markCard(pending, "created")
        gesture.succeeded()
        onCreated?.()
        onCreatedActivity?.(forStudentsReq.data ?? forStudentsReq.all_data)
        dispatch(apiIdle(activitiesService.activityTodoForStudents) as never)
    }, [
        dispatch,
        onCreated,
        onCreatedActivity,
        forStudentsReq?.success,
        forStudentsReq?.data,
        forStudentsReq?.all_data,
        gesture,
        markCard,
    ])

    useEffect(() => {
        if (awaitingCreateRef.current == null || !forStudentsReq?.error || handledCreateErrorRef.current) return
        const pending = awaitingCreateRef.current
        handledCreateErrorRef.current = true
        awaitingCreateRef.current = null
        if (pending.kind === "result") markCard(pending, "idle")
        gesture.failed(forStudentsReq.error)
        dispatchUi({ type: "FAILED", message: apiErrorMessage(forStudentsReq.error, CREATE_FAILED) })
        onCreateFailedRef.current?.(forStudentsReq.error)
        dispatch(apiIdle(activitiesService.activityTodoForStudents) as never)
    }, [dispatch, forStudentsReq?.error, gesture, markCard])

    useEffect(() => {
        if (awaitingCreateRef.current == null || !createReq?.error || handledCreateErrorRef.current) return
        const pending = awaitingCreateRef.current
        handledCreateErrorRef.current = true
        awaitingCreateRef.current = null
        if (pending.kind === "result") markCard(pending, "idle")
        gesture.failed(createReq.error)
        dispatchUi({ type: "FAILED", message: apiErrorMessage(createReq.error, CREATE_FAILED) })
        onCreateFailedRef.current?.(createReq.error)
        dispatch(apiIdle(activitiesService.createActivityAsTutor) as never)
    }, [dispatch, createReq?.error, gesture, markCard])

    useEffect(
        () => () => {
            dispatch(apiIdle(activitiesService.createActivityAsTutor) as never)
            if (forStudentsRef.current) dispatch(apiIdle(activitiesService.activityTodoForStudents) as never)
            dispatch(apiIdle(chaptersService.getChaptersAsTutor) as never)
            dispatch(apiIdle(chaptersService.getChapterSkillParts) as never)
            dispatch(apiIdle(materialsService.analyzeMaterial) as never)
        },
        [dispatch]
    )

    // ── Les gestes.
    const open = useCallback(() => {
        // Activité commune : la classe commune des élèves, et les maths par défaut (`defaultScope` du legacy
        // sans élève : « Maths as the catch-all … in multi-student mode »).
        const lastSubjectId = forStudents ? null : lastFinishedActivitySubjectId(student?.activities)
        const studentClass = forStudents ? targetClass : (student?.profile?.class ?? null)
        dispatchUi({
            type: "OPEN",
            draft: {
                ...emptyGenerationDraft,
                className: studentClass,
                subjectId: lastSubjectId != null ? String(lastSubjectId) : DEFAULT_SCHOOL_SUBJECT_ID,
                duration: defaultMaterialDuration(studentClass),
            },
        })
        lotRef.current += 1
        setResults([])
        setAnalyzed(null)
        setAnalysisStatus("idle")
        setSelectedBySam(false)
        setCrossClass(null)
        setCrossClassDismissed(false)
        startedUploadRef.current = null
        appliedAnalysisRef.current = null
        gesture.opened()
    }, [student, gesture, forStudents, targetClass])

    const close = useCallback(() => {
        awaitingCreateRef.current = null
        startedUploadRef.current = null
        dispatchUi({ type: "CLOSE" })
    }, [])

    const pickFile = useCallback(async () => {
        const result = await DocumentPicker.getDocumentAsync({ type: ACCEPTED_FILE_TYPES })
        const asset = result.canceled ? null : result.assets?.[0]
        if (asset == null) return
        const file = { uri: asset.uri, name: asset.name, mimeType: asset.mimeType ?? undefined }
        if (holdFileRef.current?.(file) === true) return
        dispatchUi({ type: "PICK_FILE", file })
    }, [])

    const launchCards = useCallback(
        (cards: { id: number; slot: SlotKey; variationLabel?: string }[], run: (slot: SlotKey) => void) => {
            lotRef.current += 1
            slotCardRef.current = {}
            for (const card of cards) slotCardRef.current[card.slot] = card.id
            materialByCardRef.current = {}
            setResults(
                cards.map((card) => ({ id: card.id, status: "pending" as const, variationLabel: card.variationLabel }))
            )
            dispatchUi({ type: "SHOW_RESULTS" })
            for (const card of cards) run(card.slot)
        },
        []
    )

    // Le backend sert d'abord les énoncés que sa réserve a déjà pour ces paramètres ;
    // `forceGeneration` (« Autres énoncés ») la saute.
    const generateFormCards = useCallback(
        (forceGeneration: boolean) => {
            const body = {
                ...generateMaterialsBody(draftRef.current, {
                    studentId,
                    studentInterests,
                    batchId: newBatchId(),
                    forceGeneration,
                }),
                ...historyIds,
            }
            const routeParams = { id: studentId }
            launchCards(
                [
                    { id: 1, slot: "skills1" },
                    { id: 2, slot: "skills2" },
                    { id: 3, slot: "skills3" },
                ],
                (slot) => {
                    if (slot === "skills1") startSkills1(routeParams, body)
                    if (slot === "skills2") startSkills2(routeParams, body)
                    if (slot === "skills3") startSkills3(routeParams, body)
                }
            )
        },
        [launchCards, startSkills1, startSkills2, startSkills3, studentInterests, studentId, historyIds]
    )
    const onGenerate = useCallback(() => generateFormCards(false), [generateFormCards])
    const onRegenerate = useCallback(() => generateFormCards(true), [generateFormCards])

    const onGenerateSimilar = useCallback(
        (level: string) => {
            const materialId = analyzed?.id
            if (materialId == null) return
            const base = {
                ...similarExercisesBody(level, draftRef.current, {
                    studentId,
                    studentInterests,
                    batchId: newBatchId(),
                }),
                ...historyIds,
            }
            const similarBody = { material_id: materialId, ...base }

            if (level === "1") {
                launchCards(
                    [
                        { id: 1, slot: "conceptReview", variationLabel: "Révision notions clés" },
                        { id: 2, slot: "methodReview", variationLabel: "Révision méthode" },
                        { id: 3, slot: "same", variationLabel: "Même type" },
                    ],
                    (slot) => {
                        if (slot === "conceptReview") startConceptReview({ materialId }, { num_clusters: 1, ...base })
                        if (slot === "methodReview") startMethodReview({ materialId }, { num_clusters: 1, ...base })
                        if (slot === "same") startSame({}, similarBody)
                    }
                )
                return
            }

            launchCards(
                [
                    { id: 1, slot: "same", variationLabel: "Même type" },
                    { id: 2, slot: "otherConcepts", variationLabel: "Autre compétence" },
                    { id: 3, slot: "sameDiversified", variationLabel: "Autre type" },
                ],
                (slot) => {
                    if (slot === "same") startSame({}, similarBody)
                    if (slot === "otherConcepts") startOtherConcepts({}, similarBody)
                    if (slot === "sameDiversified") startSameDiversified({}, similarBody)
                }
            )
        },
        [
            analyzed?.id,
            launchCards,
            startConceptReview,
            startMethodReview,
            startOtherConcepts,
            startSame,
            startSameDiversified,
            studentInterests,
            studentId,
            historyIds,
        ]
    )

    const onCreateFromFile = useCallback(() => {
        const materialId = analyzed?.id
        if (materialId == null) return
        awaitingCreateRef.current = { kind: "file" }
        handledCreateErrorRef.current = false
        dispatchUi({ type: "SUBMIT" })
        const body = createFromFileBody(materialId, draftRef.current, analyzed?.title)
        dispatch(
            (forStudents
                ? apiRequest(
                      activitiesService.activityTodoForStudents,
                      {},
                      {
                          ...body,
                          student_ids: targetStudentIds,
                          live_session_id: liveSessionId ?? null,
                      }
                  )
                : apiRequest(activitiesService.createActivityAsTutor, { id: studentId }, body)) as never
        )
        gesture.submitted()
    }, [analyzed, dispatch, studentId, gesture, forStudents, targetStudentIds, liveSessionId])

    const onCreateResult = useCallback(
        (cardId: string | number) => {
            const material = materialByCardRef.current[String(cardId)]
            if (material == null || awaitingCreateRef.current != null) return
            awaitingCreateRef.current = { kind: "result", cardId, lot: lotRef.current }
            handledCreateErrorRef.current = false
            setResults((previous) =>
                previous.map((card) => (card.id === cardId ? { ...card, creation: "creating" as const } : card))
            )
            // `has_prerequisite_quiz` suit la carte quand la salle offre le quiz (`createActivity(material,
            // hasPrerequisiteQuiz)`) ; sinon `false`, comme le corps du parent.
            const body = {
                ...createResultBody(material),
                ...(quizAvailable ? { has_prerequisite_quiz: quizByCard[String(cardId)] !== false } : {}),
            }
            dispatch(
                (forStudents
                    ? apiRequest(
                          activitiesService.activityTodoForStudents,
                          {},
                          {
                              ...body,
                              student_ids: targetStudentIds,
                              live_session_id: liveSessionId ?? null,
                          }
                      )
                    : apiRequest(activitiesService.createActivityAsTutor, { id: studentId }, body)) as never
            )
            gesture.submitted()
        },
        [dispatch, studentId, gesture, quizAvailable, quizByCard, forStudents, targetStudentIds, liveSessionId]
    )

    const attachFile = useCallback((file: UIActivityFile) => dispatchUi({ type: "PICK_FILE", file }), [])

    const seedMaterial = useCallback((material: { id: string | number; title?: string | null }) => {
        setAnalyzed({ id: material.id, title: material.title ?? null })
        setAnalysisStatus("done")
    }, [])
    const clearMaterial = useCallback(() => {
        setAnalyzed(null)
        setAnalysisStatus("idle")
    }, [])

    const chaptersLoading = !!chaptersReq?.request
    // « Le chapitre est-il exigé ? » — non quand la classe et la matière n'ont aucun chapitre
    // validé : le backend en crée un à partir de ce que l'analyse a lu (`scopeHasNoChapters`).
    // Une liste qui n'a pas pu se charger ne dit pas qu'il n'y en a aucun.
    const chapterRequired = !(
        className != null &&
        subjectId != null &&
        !chaptersLoading &&
        !chaptersFailed &&
        chapters.length === 0
    )

    const catalogs = useMemo(
        () => ({
            classes: toUIActivityClassOptions(studentClasses),
            subjects: subjects.map(toUISubject),
            chapters: toUISectionedChapters(chapters),
            levels: ACTIVITY_LEVEL_OPTIONS,
            durations: ACTIVITY_DURATION_OPTIONS,
            skillParts: toUIActivitySkillParts(chapterId != null ? skillPartsByChapterId?.[String(chapterId)] : []),
        }),
        [subjects, chapters, chapterId, skillPartsByChapterId]
    )

    const analysis = useMemo<UIActivityAnalysis | undefined>(() => {
        if (analysisStatus === "idle") return undefined
        const suggestion = analysisSuggestion(analyzed)
        return {
            status: analysisStatus,
            readTitle: selectedBySam ? undefined : (suggestion.title ?? undefined),
            selectedBySam: selectedBySam || undefined,
            crossClass: crossClass ?? undefined,
            crossClassLoading: crossClassLoading || undefined,
        }
    }, [analysisStatus, analyzed, selectedBySam, crossClass, crossClassLoading])

    const resultsWithQuiz = useMemo(
        () =>
            quizAvailable
                ? results.map((card) => ({ ...card, quizEnabled: quizByCard[String(card.id)] !== false }))
                : results,
        [quizAvailable, results, quizByCard]
    )

    // Stable : le réglage déplié du live le mémorise (`settingEditor`).
    const changeDraft = useCallback((draft: UIActivityGenerationDraft) => dispatchUi({ type: "SET_DRAFT", draft }), [])

    const isCreating = useCallback(() => awaitingCreateRef.current != null, [])

    return {
        isCreating,
        open,
        attachFile,
        seedMaterial,
        clearMaterial,
        generate: {
            visible: state.visible,
            onClose: close,
            step: state.step,
            draft: state.draft,
            onDraftChange: changeDraft,
            catalogs,
            chaptersLoading,
            chaptersFailed,
            onRetryChapters: () => setChaptersAttempt((attempt) => attempt + 1),
            skillPartsLoading: !!skillPartsReq?.request,
            chapterRequired,
            studentInterests,
            scopeLabel: generationScopeLabel(state.draft, subjects),
            onPickFile: pickFile,
            file: state.file,
            onRemoveFile: () => {
                // Une lecture encore en vol ne reviendra pas poser son support ; le même fichier rejoint se relit.
                discardUploadRef.current = true
                startedUploadRef.current = null
                dispatchUi({ type: "REMOVE_FILE" })
            },
            analysis,
            // « Basculer » emmène la classe ET la matière avec le chapitre : c'est un cadrage
            // entier qui change. Poser le seul chapitre laisserait une puce que la liste
            // rechargée, celle de l'ancienne classe, ne contient pas.
            onAcceptCrossClass: () => {
                if (crossClassPick == null) return
                setCrossClass(null)
                setSelectedBySam(true)
                dispatchUi({
                    type: "SET_DRAFT",
                    draft: {
                        ...state.draft,
                        className: crossClassPick.className,
                        subjectId: crossClassPick.subjectId ?? state.draft.subjectId,
                        chapterId: crossClassPick.chapterId,
                        skillPartPositions: [],
                    },
                })
            },
            onDismissCrossClass: () => {
                setCrossClassDismissed(true)
                setCrossClass(null)
                setCrossClassPick(null)
            },
            missingField: missingGenerationField(state.draft, chapterRequired),
            canSubmit: analyzed?.id != null && (!chapterRequired || chapterId != null),
            onGenerate,
            onCreateFromFile,
            onOpenSimilar: () => dispatchUi({ type: "OPEN_SIMILAR" }),
            onGenerateSimilar,
            generating: state.generating,
            created: state.created,
            results: resultsWithQuiz,
            // Les cartes du formulaire se régénèrent ; les similaires partent d'un fichier.
            onRegenerate: state.file == null ? onRegenerate : undefined,
            // Le quiz de prérequis n'est pas offert au parent : il demande un drapeau de
            // fonctionnalité porté par la salle du tuteur et une version d'application par élève,
            // et sa surface de relecture n'est pas portée (entrée DECISIONS du 20/09).
            quizAvailable,
            onToggleResultQuiz: quizAvailable
                ? (id, next) => setQuizByCard((previous) => ({ ...previous, [String(id)]: next }))
                : undefined,
            onCreateResult,
            onBack: () => dispatchUi({ type: "BACK" }),
            error: state.error,
        },
    }
}
